// App-wide config can go here
import * as Images from './assets/index.ts'
import { Download, Video, ExternalLink } from './utils/icons'

// Environment variables
export const ALGOLIA_APPLICATION_ID = import.meta.env.VITE_ALGOLIA_APPLICATION_ID
export const ALGOLIA_API_KEY = import.meta.env.VITE_ALGOLIA_API_KEY
export const BOOKMARKS_BANNER_URL = import.meta.env.VITE_BOOKMARKS_BANNER_URL
export const ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX = import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX
export const DEFAULT_PAGE_SIZE = import.meta.env.VITE_DEFAULT_PAGE_SIZE || '10'
export const SEARCH_BANNER_URL = import.meta.env.VITE_SEARCH_BANNER_URL
export const PRIVACY_POLICY_LINK = import.meta.env.VITE_PRIVACY_POLICY_LINK
export const TERMS_CONDITIONS_LINK = import.meta.env.VITE_TERMS_CONDITIONS_LINK
export const CONTACT_LINK = import.meta.env.VITE_CONTACT_LINK
export const AI_HELP_LINK = import.meta.env.VITE_AI_HELP_LINK
export const KAIYA_MCP_URL = import.meta.env.VITE_KAIYA_MCP_URL || ''
export const MIXPANEL_PROJECT_ID = import.meta.env.VITE_MIXPANEL_PROJECT_ID
export const CHAMELEON_TOKEN = import.meta.env.VITE_CHAMELEON_TOKEN
export const CHAMELEON_ENABLED = import.meta.env.VITE_CHAMELEON_ENABLED
export const AUTH0_REDIRECT_URL = import.meta.env.VITE_AUTH0_REDIRECT_URL
export const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID
export const AUTH0_CLIENT_DOMAIN = import.meta.env.VITE_AUTH0_CLIENT_DOMAIN
export const EMBARGOED_ASSETS_ENABLED = import.meta.env.VITE_EMBARGOED_ASSETS_ENABLED

// QA Authentication Bypass - SECURITY: Only allowed in development mode
const environment = import.meta.env.VITE_ENVIRONMENT || 'development'
const isProduction = environment === 'production'
const rawSkipQaSignOn = import.meta.env.VITE_SKIP_QA_SIGN_ON === 'true'

// Throw error if QA bypass is attempted in production
if (isProduction && rawSkipQaSignOn) {
  throw new Error(
    'SECURITY ERROR: QA authentication bypass (VITE_SKIP_QA_SIGN_ON) is not allowed in production mode. ' +
    'This setting bypasses all authentication and poses a critical security risk. ' +
    `Current environment: ${environment}`
  )
}

export const SKIP_QA_SIGN_ON = rawSkipQaSignOn && !isProduction
// Export the environment for use elsewhere
export const ENVIRONMENT = environment
export const QA_USER_ID = import.meta.env.VITE_QA_USER_ID || null
export const SUPPORTED_LOCALES = import.meta.env.VITE_LOCALES
export const ALGOLIA_AUTOCOMPLETE_SEARCH_INDEXES = [
  import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX_PT,
  import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX_ZH,
  import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX_FR,
  import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX_DE,
  import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX_JA,
  import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX_ES,
  import.meta.env.VITE_ALGOLIA_AUTOCOMPLETE_SEARCH_INDEX_PL
]
export const ADMIN_URL = import.meta.env.VITE_ADMIN_URL
export const SIGNUP_FLOW_V2_ENABLED = import.meta.env.VITE_SIGNUP_FLOW_V2 === 'true'
export const AUTH0_DB_CONNECTION_NAME = 'Username-Password-Authentication'
export const S3_UPLOAD_URL = import.meta.env.VITE_S3_UPLOAD_URL

export const AUTH0_SUPPORTED_ERROR_CODES = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
]

export const CONTENTFUL_ASSETS_HOSTNAMES = [
  'images.secure.ctfassets.net',
  'videos.secure.ctfassets.net',
  'assets.secure.ctfassets.net',
  'downloads.secure.ctfassets.net'
]

export const AUTH0_CHECK_CEILING = import.meta.env.VITE_AUTH0_CHECK_CEILING

// Resource type constants
export const ResourceTypes = {
  CONTENT_BLOCK: 'Content Block',
  CONTENT_PAGE: 'Program Unit',
  DOWNLOAD: 'Download',
  LINK_TO_PROXIMA: 'Link to Proxima',
  LINK_TO_RESEARCH_HUB: 'Link to Research Hub',
  LINK_TO_INSTRUCTOR_HUB: 'Link to Instructor Hub',
  LINK_TO_KNOWLEDGE_HUB: 'Link to Knowledge Hub',
  MODAL: 'Modal',
  VIDEO: 'Video',
}

// export type ResourceType = typeof ResourceTypes[keyof typeof ResourceTypes]

export const RelatedFileIcons = {
  [ResourceTypes.DOWNLOAD]: Download,
  [ResourceTypes.LINK_TO_PROXIMA]: ExternalLink,
  [ResourceTypes.LINK_TO_RESEARCH_HUB]: ExternalLink,
  [ResourceTypes.LINK_TO_INSTRUCTOR_HUB]: ExternalLink,
  [ResourceTypes.LINK_TO_KNOWLEDGE_HUB]: ExternalLink,
  [ResourceTypes.VIDEO]: Video,
}

// App constants
export const INSTRUCTOR_HUB = 'instructorHub'
export const KNOWLEDGE_HUB = 'knowledgeHub'
export const RESEARCH_HUB = 'researchHub'
export const PREWORK = 'prework'
export const PORTAL = 'portal'
export const PROXIMA = 'proxima'
export const CHANGECOACH = 'changeCoach'

// export type AppCode = typeof INSTRUCTOR_HUB | typeof KNOWLEDGE_HUB | typeof RESEARCH_HUB | 
//   typeof PREWORK | typeof PORTAL | typeof PROXIMA | typeof CHANGECOACH

export const APP_PATH_MAPPING = {
  [INSTRUCTOR_HUB]: import.meta.env.VITE_INSTRUCTOR_HUB_ROOT_URL,
  [KNOWLEDGE_HUB]: import.meta.env.VITE_KNOWLEDGE_HUB_ROOT_URL,
  [PREWORK]: import.meta.env.VITE_PREWORK_ROOT_URL,
  [PORTAL]: import.meta.env.VITE_PROSCI_PORTAL_ROOT_URL,
  [PROXIMA]: import.meta.env.VITE_PROXIMA_ROOT_URL,
  [RESEARCH_HUB]: import.meta.env.VITE_RESEARCH_HUB_ROOT_URL,
  [CHANGECOACH]: import.meta.env.VITE_CHANGE_COACH_ROOT_URL,
}

export const INSTRUCTOR_HUB_SLUG = "instructor"
export const KNOWLEDGE_HUB_SLUG = "knowledge"  
export const RESEARCH_HUB_SLUG = "research"

export type HubSlug = typeof INSTRUCTOR_HUB_SLUG | typeof KNOWLEDGE_HUB_SLUG | typeof RESEARCH_HUB_SLUG

export const KNOWLEDGE_HUB_BANNER = import.meta.env.VITE_KNOWLEDGE_HUB_BANNER
export const INSTRUCTOR_HUB_BANNER = import.meta.env.VITE_INSTRUCTOR_HUB_BANNER
export const RESEARCH_HUB_BANNER = import.meta.env.VITE_RESEARCH_HUB_BANNER

export const INSTRUCTOR_HUB_ROUTE = "guidance"
export const KNOWLEDGE_HUB_ROUTE = "programs"
export const RESEARCH_HUB_ROUTE = "reports"

export const INSTRUCTOR_HUB_TYPE = "Guidance"
export const KNOWLEDGE_HUB_TYPE = "Program"
export const RESEARCH_HUB_TYPE = "Report"

// interface AppMappingConfig {
//   code: AppCode
//   path: string | undefined
//   route: string
//   singular: string
//   plural: string
//   banner: string | undefined
// }

export const APP_MAPPING_BY_SLUG = {
  [INSTRUCTOR_HUB_SLUG]: {
    code: INSTRUCTOR_HUB,
    path: import.meta.env.VITE_INSTRUCTOR_HUB_ROOT_URL,
    route: INSTRUCTOR_HUB_ROUTE,
    singular: INSTRUCTOR_HUB_TYPE,
    plural: INSTRUCTOR_HUB_TYPE + "s",
    banner: INSTRUCTOR_HUB_BANNER,
  },
  [KNOWLEDGE_HUB_SLUG]: {
    code: KNOWLEDGE_HUB,
    path: import.meta.env.VITE_KNOWLEDGE_HUB_ROOT_URL,
    route: KNOWLEDGE_HUB_ROUTE,
    singular: KNOWLEDGE_HUB_TYPE,
    plural: KNOWLEDGE_HUB_TYPE + "s",
    banner: KNOWLEDGE_HUB_BANNER,
  },
  [RESEARCH_HUB_SLUG]: {
    code: RESEARCH_HUB,
    path: import.meta.env.VITE_RESEARCH_HUB_ROOT_URL,
    route: RESEARCH_HUB_ROUTE,
    singular: RESEARCH_HUB_TYPE,
    plural: RESEARCH_HUB_TYPE + "s",
    banner: RESEARCH_HUB_BANNER,
  },
}

export const APP_IMAGE_MAPPING = {
  [INSTRUCTOR_HUB]: Images.instructorHub,
  [KNOWLEDGE_HUB]: Images.knowledgeHub,
  [RESEARCH_HUB]: Images.researchHub,
  [PREWORK]: Images.prework,
  [PORTAL]: Images.prosci,
  [PROXIMA]: Images.proxima,
  [CHANGECOACH]: Images.changeCoach,
}

// interface SearchParams {
//   programProductCode: string | null
//   resourceType: string | null
//   limit: string
//   skip: number
// }

export const DEFAULT_SEARCH_PARAMS = {
  programProductCode: null,
  resourceType: null,
  limit: DEFAULT_PAGE_SIZE,
  skip: 0,
}

export const HUB_URL_MAPPINGS = {
  'Research Hub': `${APP_PATH_MAPPING[RESEARCH_HUB]}/reports/`,
  'Knowledge Hub': `${APP_PATH_MAPPING[KNOWLEDGE_HUB]}/programs/`,
  'Instructor Hub': `${APP_PATH_MAPPING[INSTRUCTOR_HUB]}/guidance/`,
}

// Form options
export const APP_MESSAGING_KEYS = {
  error: 'unexpectedError',
  noEmpty: 'cannotBeEmpty',
  updateSuccessful: 'successfullyUpdated',
  saveSuccessful: 'successfullySaved',
  deleteSuccessful: 'successfullyDeleted',
  charLimit: 'characterLimitExceeded',
  autoSaving: 'autosaving',
  autoSaved: 'autosaved',
  completed: 'successfullyCompleted',
}

// export type AppMessagingKey = keyof typeof APP_MESSAGING_KEYS

// interface ButtonLoadingState {
//   value: boolean
//   button: string | null
// }

export const InitialButtonLoadingState = {
  value: false,
  button: null,
}

// Button styles
export const BUTTON_PRIMARY_CENTER = 'button-primary center'
export const BUTTON_PRIMARY_OUTLINE_CENTER = 'button-primary-outline center'
export const IN_PROGRESS = 'in-progress'

// Input limits
export const STRING_INPUT_MAX = 255
export const SHORT_STRING_INPUT_MAX = 50
export const TEXT_INPUT_MAX = 32768

export const KAIYA_PROMPT_CATEGORIES = [
  'Practitioners', 
  'Sponsors', 
  'Managers', 
  'Prompt Generator'
]

// export type KaiyaPromptCategory = typeof KAIYA_PROMPT_CATEGORIES[number]